import { useCallback, useRef, useEffect } from 'react';

// Soft, pleasant casino-style sounds using pure sine waves
const SOUNDS = {
  // Soft click - gentle high pitched ping
  click: { freq: 1800, duration: 0.04, type: 'sine', volume: 0.15 },
  // Coin collect - bright cheerful ding
  coin: { notes: [1047, 1319], duration: 0.08, type: 'sine', volume: 0.2 },
  // Soft tick for counters
  tick: { freq: 880, duration: 0.025, type: 'sine', volume: 0.1 },
  // Reel stop - soft thump
  stop: { freq: 220, duration: 0.06, type: 'sine', volume: 0.15 },
  // Spin start - gentle whoosh (rising tone)
  spin: { notes: [330, 440, 550], duration: 0.05, type: 'sine', volume: 0.12 },
  // Win - happy ascending chime (C-E-G-C major chord arpeggio)
  win: { notes: [523, 659, 784, 1047], duration: 0.12, type: 'sine', volume: 0.25 },
  // Big win - extended celebration
  bigWin: { notes: [523, 659, 784, 1047, 1319, 1568], duration: 0.1, type: 'sine', volume: 0.3 },
  // Jackpot - triumphant fanfare
  jackpot: { notes: [523, 659, 784, 1047, 784, 1047, 1319, 1568, 2093], duration: 0.12, type: 'sine', volume: 0.35 },
  // Lose - soft descending tone (not harsh)
  lose: { notes: [440, 392, 349], duration: 0.15, type: 'sine', volume: 0.15 },
  // Level up - bright ascending
  levelUp: { notes: [523, 659, 784, 1047], duration: 0.15, type: 'sine', volume: 0.25 },
  // Crash game tick - subtle pulse
  crashTick: { freq: 660, duration: 0.015, type: 'sine', volume: 0.08 },
  // Cashout success
  cashout: { notes: [784, 988, 1175], duration: 0.1, type: 'sine', volume: 0.25 },
};

export const useSounds = (enabled = true) => {
  const audioCtxRef = useRef(null);
  const volumeRef = useRef(0.3);

  useEffect(() => {
    return () => {
      if (audioCtxRef.current) {
        audioCtxRef.current.close();
      }
    };
  }, []);

  const getAudioContext = useCallback(() => {
    if (!audioCtxRef.current || audioCtxRef.current.state === 'closed') {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume();
    }
    return audioCtxRef.current;
  }, []);

  const playTone = useCallback((freq, duration, type = 'sine', volume = 0.2) => {
    if (!enabled) return;
    try {
      const ctx = getAudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = type;
      osc.frequency.value = freq;

      // Soft attack and release for pleasant sound
      const vol = volume * volumeRef.current;
      gain.gain.setValueAtTime(0, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(vol, ctx.currentTime + 0.01); // soft attack
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration); // smooth decay

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + duration + 0.05);
    } catch (e) {
      // Silent fail if audio not supported
    }
  }, [enabled, getAudioContext]);

  const playSequence = useCallback((notes, duration, type = 'sine', volume = 0.2) => {
    if (!enabled) return;
    notes.forEach((note, i) => {
      setTimeout(() => playTone(note, duration, type, volume), i * duration * 1000 * 0.7);
    });
  }, [enabled, playTone]);

  const play = useCallback((soundName) => {
    if (!enabled) return;
    const sound = SOUNDS[soundName];
    if (!sound) return;

    const vol = sound.volume || 0.2;
    if (sound.notes) {
      playSequence(sound.notes, sound.duration, sound.type, vol);
    } else {
      playTone(sound.freq, sound.duration, sound.type, vol);
    }
  }, [enabled, playTone, playSequence]);

  const spinSound = useCallback(() => {
    if (!enabled) return;
    // Gentle ascending tones for spin start
    playTone(440, 0.06, 'sine', 0.1);
    setTimeout(() => playTone(550, 0.06, 'sine', 0.08), 50);
    setTimeout(() => playTone(660, 0.06, 'sine', 0.06), 100);
  }, [enabled, playTone]);

  const reelStopSound = useCallback((reelIndex) => {
    if (!enabled) return;
    // Soft satisfying click for each reel stop
    const baseFreq = 600 - reelIndex * 50;
    playTone(baseFreq, 0.08, 'sine', 0.15);
  }, [enabled, playTone]);

  // Rocket flight ambient sound - soft sparkle/whoosh
  const rocketOscRef = useRef(null);
  const rocketGainRef = useRef(null);

  const startRocketSound = useCallback(() => {
    if (!enabled) return;
    try {
      const ctx = getAudioContext();

      // Create gentle oscillator for ambient hum
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.value = 220; // Low gentle hum

      gain.gain.value = 0;
      gain.gain.linearRampToValueAtTime(0.04 * volumeRef.current, ctx.currentTime + 0.3);

      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();

      rocketOscRef.current = osc;
      rocketGainRef.current = gain;
    } catch (e) {}
  }, [enabled, getAudioContext]);

  const updateRocketSound = useCallback((multiplier) => {
    if (!enabled || !rocketOscRef.current || !rocketGainRef.current) return;
    try {
      // Gradually increase pitch as multiplier grows (exciting!)
      const freq = 220 + Math.min(multiplier * 15, 200);
      rocketOscRef.current.frequency.value = freq;

      // Soft sparkle every whole number
      if (Math.floor(multiplier) > Math.floor(multiplier - 0.1)) {
        playTone(800 + multiplier * 20, 0.05, 'sine', 0.08);
      }
    } catch (e) {}
  }, [enabled, playTone]);

  const stopRocketSound = useCallback(() => {
    try {
      if (rocketGainRef.current) {
        const ctx = getAudioContext();
        rocketGainRef.current.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.2);
      }
      if (rocketOscRef.current) {
        setTimeout(() => {
          try { rocketOscRef.current?.stop(); } catch {}
          rocketOscRef.current = null;
          rocketGainRef.current = null;
        }, 250);
      }
    } catch (e) {}
  }, [getAudioContext]);

  const setVolume = useCallback((vol) => {
    volumeRef.current = Math.max(0, Math.min(1, vol));
  }, []);

  return {
    play,
    spinSound,
    reelStopSound,
    startRocketSound,
    updateRocketSound,
    stopRocketSound,
    setVolume,
    playTone,
  };
};

export default useSounds;
